import { detectPadFamily, type PadFamily } from '../input-glyphs.js';

export type GamepadAction = 'camera' | 'settings';

export interface GamepadStatus {
  connected: boolean;
  index: number | null;
  name: string;
  standardMapping: boolean;
  mapping?: GamepadMapping;
  mappingSource?: 'standard' | 'legacy-default' | 'custom';
  axisCount?: number;
  buttonCount?: number;
  liveAxes?: readonly number[];
  pressedButtons?: readonly number[];
  /** Analogue value of every button (0..1), present while diagnostics are on. */
  liveButtons?: readonly number[];
  family?: PadFamily;
  /** True when the browser exposes dual-rumble or pulse haptics for this pad. */
  rumble?: boolean;
}

/** Player tuning from the CONTROLS tab; defaults match the original feel. */
export interface GamepadTuning {
  /** Inner steering dead zone, 0..0.4 of stick travel. */
  steerDeadZone: number;
  /** Response exponent: <1 reaches lock sooner, >1 adds centre precision. */
  steerLinearity: number;
  /** Trigger creep filter, 0..0.3 of travel. */
  triggerDeadZone: number;
  /** Rumble strength multiplier, 0 disables controller vibration. */
  vibration: number;
}

export type GamepadCaptureKind = 'button' | 'analog' | 'axis';

export type GamepadCaptureResult =
  | { kind: 'button'; index: number }
  | { kind: 'axis'; index: number; direction: 1 | -1; resting: number };

interface GamepadCapture {
  kind: GamepadCaptureKind;
  armed: boolean;
  restingAxes: number[];
  startedAt: number;
  resolve: (result: GamepadCaptureResult | null) => void;
}

export type GamepadAnalogSource = 'button' | 'axis-positive' | 'axis-negative' | 'axis-range';

export interface GamepadAnalogBinding {
  source: GamepadAnalogSource;
  index: number;
}

export interface GamepadMapping {
  version: 1;
  steerAxis: number;
  steerInverted: boolean;
  throttle: GamepadAnalogBinding;
  brake: GamepadAnalogBinding;
  handbrakeButton: number;
  nitroButton: number;
  fireButton: number;
  cameraButton: number;
  turretAxis: number;
  turretInverted: boolean;
  resetButton: number;
  settingsButton: number;
}

export interface GamepadDriveInput {
  connected: boolean;
  throttle: number;
  brake: number;
  steer: number;
  handbrake: boolean;
  nitro: boolean;
  turret: number;
  fire: boolean;
  reset: boolean;
  /** Fixed L3 fallback for Heat Run banking; it does not steal a face button
   * from nitro, drift, camera or combat on older standard pads. */
  bank: boolean;
}

export interface GamepadButtonLike {
  pressed?: boolean;
  value?: number;
}

export interface GamepadLike {
  axes: readonly number[];
  buttons: readonly GamepadButtonLike[];
  connected?: boolean;
  id: string;
  index: number;
  mapping: string;
  vibrationActuator?: GamepadHapticActuator | null;
  hapticActuators?: readonly GamepadHapticActuator[];
}

interface MappedGamepadInput extends GamepadDriveInput {
  camera: boolean;
  settings: boolean;
}

const EMPTY_DRIVE_INPUT = Object.freeze({
  connected: false,
  throttle: 0,
  brake: 0,
  steer: 0,
  handbrake: false,
  nitro: false,
  turret: 0,
  fire: false,
  reset: false,
  bank: false,
}) satisfies GamepadDriveInput;

export const DEFAULT_GAMEPAD_TUNING: Readonly<GamepadTuning> = Object.freeze({
  steerDeadZone: 0.14,
  steerLinearity: 1,
  triggerDeadZone: 0.06,
  vibration: 1,
});
/** Stick travel beyond this reads as full lock, so worn sticks still reach 1. */
const STEER_OUTER_DEAD_ZONE = 0.04;
const CAPTURE_TIMEOUT_MS = 6_000;

const GAMEPAD_MAPPING_STORAGE_KEY = 'nightshift:gamepad-mappings:v1';
// Reads happen once per frame; ~0.5 s of neutrality before a second pad may
// take over prevents single-frame control migration.
const GAMEPAD_MIGRATION_NEUTRAL_READS = 30;
const DEFAULT_GAMEPAD_MAPPING: GamepadMapping = {
  version: 1,
  steerAxis: 0,
  steerInverted: true,
  throttle: { source: 'button', index: 7 },
  brake: { source: 'button', index: 6 },
  handbrakeButton: 0,
  nitroButton: 1,
  fireButton: 2,
  cameraButton: 3,
  turretAxis: 2,
  turretInverted: true,
  resetButton: 8,
  settingsButton: 9,
};

export class GamepadController {
  private activeIndex: number | null = null;
  private inactiveReads = 0;
  private bound = false;
  private previousCamera = false;
  private previousReset = false;
  private previousSettings = false;
  private mappingId = '';
  private gamepadDescriptor = '';
  private mapping: GamepadMapping = defaultGamepadMapping();
  private mappingSource: GamepadStatus['mappingSource'] = 'legacy-default';
  private statusRevision = 0;
  private emittedStatusRevision = -1;
  private diagnosticsEnabled = false;
  private lastDiagnosticAt = Number.NEGATIVE_INFINITY;
  private lastDiagnosticKey = '';
  private lastLiveAxes: readonly number[] = [];
  private lastPressedButtons: readonly number[] = [];
  private tuning: GamepadTuning = { ...DEFAULT_GAMEPAD_TUNING };
  private capture: GamepadCapture | null = null;
  /** Starts true: only a real disabled → enabled transition gates held inputs. */
  private wasDriving = true;
  /** Inputs still held from a menu press; ignored until released once. */
  private readonly heldFromMenu = { throttle: false, brake: false, handbrake: false, nitro: false, fire: false };
  private lastLiveButtons: readonly number[] = [];
  private readonly mappingCache = new Map<string, { mapping: GamepadMapping; custom: boolean }>();
  private readonly mappedInput = createMappedGamepadInput();
  private readonly intentInput = createMappedGamepadInput();
  /** Ephemeral output reused until the next read; InputController copies it
   * immediately into the timestamped PlayerInput sent to prediction/network. */
  private readonly driveInput: GamepadDriveInput = { ...EMPTY_DRIVE_INPUT };

  constructor(
    private readonly onStatus?: (status: GamepadStatus) => void,
    private readonly onAction?: (action: GamepadAction) => void,
  ) {}

  bind(): void {
    if (this.bound) return;
    this.bound = true;
    window.addEventListener('gamepadconnected', this.onConnected);
    window.addEventListener('gamepaddisconnected', this.onDisconnected);
    this.currentGamepad();
  }

  read(drivingEnabled: boolean): GamepadDriveInput {
    const gamepad = this.currentGamepad();
    if (!gamepad) {
      this.clearButtons();
      if (this.capture && performance.now() - this.capture.startedAt > CAPTURE_TIMEOUT_MS) this.finishCapture(null);
      return EMPTY_DRIVE_INPUT;
    }
    if (this.capture) {
      this.pollCapture(gamepad);
      this.wasDriving = false;
      return EMPTY_DRIVE_INPUT;
    }

    const mapped = mapGamepadInputInto(gamepad, this.mapping, this.mappedInput, this.tuning);
    if (mapped.settings && !this.previousSettings) this.onAction?.('settings');
    if (drivingEnabled && mapped.camera && !this.previousCamera) this.onAction?.('camera');
    const reset = drivingEnabled && mapped.reset && !this.previousReset;
    this.previousCamera = mapped.camera;
    this.previousReset = mapped.reset;
    this.previousSettings = mapped.settings;
    if (!drivingEnabled) {
      this.wasDriving = false;
      return EMPTY_DRIVE_INPUT;
    }
    // A confirm/back press that closed a menu must not leak into the car as
    // a drift or nitro burst: inputs held at the moment driving resumes are
    // ignored until they are released once.
    if (!this.wasDriving) {
      this.wasDriving = true;
      this.heldFromMenu.throttle = mapped.throttle > 0.1;
      this.heldFromMenu.brake = mapped.brake > 0.1;
      this.heldFromMenu.handbrake = mapped.handbrake;
      this.heldFromMenu.nitro = mapped.nitro;
      this.heldFromMenu.fire = mapped.fire;
    }
    if (this.heldFromMenu.throttle && mapped.throttle <= 0.1) this.heldFromMenu.throttle = false;
    if (this.heldFromMenu.brake && mapped.brake <= 0.1) this.heldFromMenu.brake = false;
    if (this.heldFromMenu.handbrake && !mapped.handbrake) this.heldFromMenu.handbrake = false;
    if (this.heldFromMenu.nitro && !mapped.nitro) this.heldFromMenu.nitro = false;
    if (this.heldFromMenu.fire && !mapped.fire) this.heldFromMenu.fire = false;
    this.driveInput.connected = true;
    this.driveInput.throttle = this.heldFromMenu.throttle ? 0 : mapped.throttle;
    this.driveInput.brake = this.heldFromMenu.brake ? 0 : mapped.brake;
    this.driveInput.steer = mapped.steer;
    this.driveInput.handbrake = !this.heldFromMenu.handbrake && mapped.handbrake;
    this.driveInput.nitro = !this.heldFromMenu.nitro && mapped.nitro;
    this.driveInput.turret = mapped.turret;
    this.driveInput.fire = !this.heldFromMenu.fire && mapped.fire;
    this.driveInput.reset = reset;
    this.driveInput.bank = mapped.bank;
    return this.driveInput;
  }

  setTuning(tuning: Partial<GamepadTuning>): void {
    this.tuning = normalizeGamepadTuning({ ...this.tuning, ...tuning });
  }

  get currentTuning(): Readonly<GamepadTuning> {
    return this.tuning;
  }

  /**
   * Waits for the player to press a button (or push an axis) to bind. The
   * capture arms only after every input is released, so the confirm press
   * that opened it is never captured. Resolves null on timeout or cancel.
   */
  beginCapture(kind: GamepadCaptureKind): Promise<GamepadCaptureResult | null> {
    this.finishCapture(null);
    return new Promise((resolve) => {
      this.capture = { kind, armed: false, restingAxes: [], startedAt: performance.now(), resolve };
      this.clearButtons();
    });
  }

  cancelCapture(): void {
    this.finishCapture(null);
  }

  get capturing(): boolean {
    return this.capture !== null;
  }

  private pollCapture(gamepad: GamepadLike): void {
    const capture = this.capture;
    if (!capture) return;
    if (performance.now() - capture.startedAt > CAPTURE_TIMEOUT_MS) {
      this.finishCapture(null);
      return;
    }
    const pressed = gamepad.buttons.findIndex((button) => Boolean(button?.pressed) || Number(button?.value) > 0.5);
    if (!capture.armed) {
      if (pressed === -1) {
        capture.armed = true;
        // Triggers on some legacy mappings rest at -1, so axes are compared
        // against their own resting value rather than zero.
        capture.restingAxes = gamepad.axes.map(axisNumber);
      }
      return;
    }
    if (capture.kind !== 'axis' && pressed !== -1) {
      this.finishCapture({ kind: 'button', index: pressed });
      return;
    }
    if (capture.kind === 'button') return;
    let best = -1;
    let bestDelta = 0;
    gamepad.axes.forEach((value, index) => {
      const delta = axisNumber(value) - (capture.restingAxes[index] ?? 0);
      if (Math.abs(delta) > Math.abs(bestDelta)) {
        bestDelta = delta;
        best = index;
      }
    });
    if (best >= 0 && Math.abs(bestDelta) > 0.6) {
      this.finishCapture({
        kind: 'axis',
        index: best,
        direction: bestDelta > 0 ? 1 : -1,
        resting: capture.restingAxes[best] ?? 0,
      });
    }
  }

  private finishCapture(result: GamepadCaptureResult | null): void {
    const capture = this.capture;
    if (!capture) return;
    this.capture = null;
    // Swallow the binding press so it does not immediately fire as an action.
    this.previousCamera = true;
    this.previousReset = true;
    this.previousSettings = true;
    capture.resolve(result);
  }

  setMapping(mapping: GamepadMapping): void {
    const gamepad = this.currentGamepad();
    if (!gamepad) return;
    this.mapping = normalizeGamepadMapping(mapping);
    this.mappingSource = 'custom';
    saveGamepadMapping(gamepad.id, this.mapping);
    this.mappingCache.set(normalizedGamepadId(gamepad.id), { mapping: this.mapping, custom: true });
    this.statusRevision += 1;
    this.emitStatus(gamepad, this.diagnosticsEnabled);
  }

  resetMapping(): void {
    const gamepad = this.currentGamepad();
    if (!gamepad) return;
    forgetGamepadMapping(gamepad.id);
    this.mapping = defaultGamepadMapping();
    this.mappingSource = gamepad.mapping === 'standard' ? 'standard' : 'legacy-default';
    this.mappingCache.set(normalizedGamepadId(gamepad.id), { mapping: this.mapping, custom: false });
    this.statusRevision += 1;
    this.emitStatus(gamepad, this.diagnosticsEnabled);
  }

  setDiagnosticsEnabled(enabled: boolean): void {
    if (enabled === this.diagnosticsEnabled) return;
    this.diagnosticsEnabled = enabled;
    this.lastDiagnosticAt = Number.NEGATIVE_INFINITY;
    this.lastDiagnosticKey = '';
    if (enabled) this.currentGamepad();
    else {
      this.lastLiveAxes = [];
      this.lastPressedButtons = [];
      this.lastLiveButtons = [];
    }
  }

  rumble(strengthValue: number, durationValue: number, heavy = false): void {
    if (this.tuning.vibration <= 0) return;
    const gamepad = this.currentGamepad();
    if (!gamepad) return;
    const actuator = gamepad.vibrationActuator ?? gamepad.hapticActuators?.[0];
    if (!actuator) return;
    const strength = Math.max(0, Math.min(1, strengthValue * this.tuning.vibration));
    const duration = Math.max(18, Math.min(320, durationValue));
    try {
      const legacyActuator = actuator as GamepadHapticActuator & {
        pulse?: (value: number, duration: number) => Promise<unknown> | unknown;
      };
      const result = actuator.playEffect
        ? actuator.playEffect('dual-rumble', {
            duration,
            startDelay: 0,
            strongMagnitude: strength * (heavy ? 1 : 0.68),
            weakMagnitude: strength * (heavy ? 0.62 : 1),
          })
        : legacyActuator.pulse?.(strength, duration);
      swallow(result);
    } catch {
      // Haptic support varies between browsers and controller firmware.
    }
  }

  /**
   * Impulse-trigger feedback (Xbox pads on Chromium): a pulse in the brake
   * trigger for lock-up or a braking cue, in the throttle trigger for
   * wheelspin. Silently does nothing on pads without trigger motors.
   */
  rumbleTriggers(leftValue: number, rightValue: number, durationValue: number): void {
    if (this.tuning.vibration <= 0) return;
    const gamepad = this.currentGamepad();
    const actuator = gamepad?.vibrationActuator as (GamepadHapticActuator & { effects?: readonly string[] }) | null | undefined;
    if (!actuator?.playEffect || !actuator.effects?.includes('trigger-rumble')) return;
    const scale = this.tuning.vibration;
    try {
      swallow(actuator.playEffect('trigger-rumble' as GamepadHapticEffectType, {
        duration: Math.max(18, Math.min(240, durationValue)),
        startDelay: 0,
        leftTrigger: Math.max(0, Math.min(1, leftValue * scale)),
        rightTrigger: Math.max(0, Math.min(1, rightValue * scale)),
      } as GamepadEffectParameters));
    } catch {
      // Older Chromium builds list the effect but reject its parameters.
    }
  }

  destroy(): void {
    if (!this.bound) return;
    this.bound = false;
    window.removeEventListener('gamepadconnected', this.onConnected);
    window.removeEventListener('gamepaddisconnected', this.onDisconnected);
    this.activeIndex = null;
    this.inactiveReads = 0;
    this.mappingId = '';
    this.gamepadDescriptor = '';
    this.finishCapture(null);
    this.clearButtons();
  }

  private currentGamepad(): GamepadLike | null {
    const gamepads = readBrowserGamepads();
    let gamepad = this.activeIndex === null ? null : gamepads[this.activeIndex] ?? null;
    if (!isConnected(gamepad)) {
      gamepad = gamepads.find((candidate) => (
        isConnected(candidate)
        && hasDrivingIntent(candidate, this.mappingFor(candidate).mapping, this.intentInput, this.tuning)
      )) ?? gamepads.find(isConnected) ?? null;
      this.activeIndex = gamepad?.index ?? null;
      this.inactiveReads = 0;
    } else {
      let hasAlternative = false;
      for (const candidate of gamepads) {
        if (isConnected(candidate) && candidate.index !== gamepad.index) {
          hasAlternative = true;
          break;
        }
      }
      // Preserve the allocation-light single-controller path. Only inspect
      // mappings for other pads when there actually is another connected pad.
      // Require a sustained lack of intent before migrating: a single neutral
      // frame (hands off the pad mid-straight) must not hand control to a
      // second pad's resting-finger trigger or stick drift.
      if (hasAlternative && !hasDrivingIntent(gamepad, this.mapping, this.intentInput, this.tuning)) {
        this.inactiveReads += 1;
        if (this.inactiveReads >= GAMEPAD_MIGRATION_NEUTRAL_READS) {
          const activeCandidate = gamepads.find((candidate) => (
            isConnected(candidate)
            && candidate.index !== gamepad?.index
            && hasDrivingIntent(candidate, this.mappingFor(candidate).mapping, this.intentInput, this.tuning)
          ));
          if (activeCandidate) {
            gamepad = activeCandidate;
            this.activeIndex = activeCandidate.index;
            this.inactiveReads = 0;
            this.clearButtons();
          }
        }
      } else {
        this.inactiveReads = 0;
      }
    }
    this.syncMapping(gamepad);
    this.emitStatus(gamepad, this.diagnosticsEnabled);
    return gamepad;
  }

  private syncMapping(gamepad: GamepadLike | null): void {
    const id = gamepad ? normalizedGamepadId(gamepad.id) : '';
    const descriptor = gamepad ? `${id}:${gamepad.index}:${gamepad.mapping}` : '';
    if (descriptor !== this.gamepadDescriptor) {
      this.gamepadDescriptor = descriptor;
      this.statusRevision += 1;
    }
    if (id === this.mappingId) return;
    this.mappingId = id;
    const cached = gamepad ? this.mappingFor(gamepad) : null;
    this.mapping = cached?.mapping ?? defaultGamepadMapping();
    this.mappingSource = cached?.custom
      ? 'custom'
      : gamepad?.mapping === 'standard' ? 'standard' : 'legacy-default';
  }

  private mappingFor(gamepad: GamepadLike): { mapping: GamepadMapping; custom: boolean } {
    const id = normalizedGamepadId(gamepad.id);
    const cached = this.mappingCache.get(id);
    if (cached) return cached;
    const stored = loadGamepadMapping(gamepad.id);
    const entry = { mapping: stored ?? defaultGamepadMapping(), custom: stored !== null };
    this.mappingCache.set(id, entry);
    return entry;
  }

  private emitStatus(gamepad: GamepadLike | null, includeDiagnostics = false): void {
    const now = typeof performance === 'undefined' ? Date.now() : performance.now();
    const refreshDiagnostics = includeDiagnostics && now - this.lastDiagnosticAt >= 40;
    if (!refreshDiagnostics && this.emittedStatusRevision === this.statusRevision) return;
    if (refreshDiagnostics && gamepad) {
      this.lastDiagnosticAt = now;
      this.lastLiveAxes = gamepad.axes.map((value) => Math.round(axisNumber(value) * 100) / 100);
      this.lastPressedButtons = gamepad.buttons.flatMap((button, index) => (
        button?.pressed || Number(button?.value) > 0.5 ? [index] : []
      ));
      this.lastLiveButtons = gamepad.buttons.map((_, index) => Math.round(buttonValue(gamepad, index) * 100) / 100);
    } else if (!gamepad) {
      this.lastLiveAxes = [];
      this.lastPressedButtons = [];
      this.lastLiveButtons = [];
    }
    const liveAxes = gamepad && includeDiagnostics ? this.lastLiveAxes : undefined;
    const pressedButtons = gamepad && includeDiagnostics ? this.lastPressedButtons : undefined;
    const liveButtons = gamepad && includeDiagnostics ? this.lastLiveButtons : undefined;
    const diagnosticKey = refreshDiagnostics
      ? `${liveAxes?.join(',') ?? ''}:${liveButtons?.join(',') ?? ''}`
      : this.lastDiagnosticKey;
    if (this.emittedStatusRevision === this.statusRevision && diagnosticKey === this.lastDiagnosticKey) return;
    const status: GamepadStatus = gamepad
      ? {
          connected: true,
          index: gamepad.index,
          name: gamepadName(gamepad),
          standardMapping: gamepad.mapping === 'standard',
          mapping: {
            ...this.mapping,
            throttle: { ...this.mapping.throttle },
            brake: { ...this.mapping.brake },
          },
          mappingSource: this.mappingSource,
          axisCount: gamepad.axes.length,
          buttonCount: gamepad.buttons.length,
          liveAxes,
          pressedButtons,
          liveButtons,
          family: detectPadFamily(gamepad.id),
          rumble: Boolean(gamepad.vibrationActuator ?? gamepad.hapticActuators?.[0]),
        }
      : { connected: false, index: null, name: '', standardMapping: false };
    this.emittedStatusRevision = this.statusRevision;
    this.lastDiagnosticKey = diagnosticKey;
    this.onStatus?.(status);
  }

  private readonly onConnected = (event: Event): void => {
    const gamepad = (event as GamepadEvent).gamepad;
    if (this.activeIndex === null) this.activeIndex = gamepad.index;
    this.currentGamepad();
  };

  private readonly onDisconnected = (event: Event): void => {
    const gamepad = (event as GamepadEvent).gamepad;
    if (this.activeIndex === gamepad.index) this.activeIndex = null;
    this.currentGamepad();
  };

  private clearButtons(): void {
    this.previousCamera = false;
    this.previousReset = false;
    this.previousSettings = false;
  }
}

export function mapGamepadInput(
  gamepad: GamepadLike,
  mapping: GamepadMapping = DEFAULT_GAMEPAD_MAPPING,
  tuning: GamepadTuning = DEFAULT_GAMEPAD_TUNING,
): MappedGamepadInput {
  const normalized = normalizeGamepadMapping(mapping);
  return mapGamepadInputInto(gamepad, normalized, createMappedGamepadInput(), normalizeGamepadTuning(tuning));
}

function mapGamepadInputInto(
  gamepad: GamepadLike,
  normalized: GamepadMapping,
  result: MappedGamepadInput,
  tuning: GamepadTuning = DEFAULT_GAMEPAD_TUNING,
): MappedGamepadInput {
  const stickSteer = shapeSteeringAxis(axisValue(gamepad, normalized.steerAxis), tuning)
    * (normalized.steerInverted ? -1 : 1);
  const dpadSteer = buttonValue(gamepad, 14) - buttonValue(gamepad, 15);
  const rightStickTurret = applyGamepadDeadZone(axisValue(gamepad, normalized.turretAxis), 0.18)
    * (normalized.turretInverted ? -1 : 1);
  const bumperTurret = buttonValue(gamepad, 4) - buttonValue(gamepad, 5);
  result.connected = true;
  result.throttle = Math.max(analogBindingValue(gamepad, normalized.throttle, tuning.triggerDeadZone), buttonValue(gamepad, 12));
  result.brake = Math.max(analogBindingValue(gamepad, normalized.brake, tuning.triggerDeadZone), buttonValue(gamepad, 13));
  result.steer = strongest(stickSteer, dpadSteer);
  result.handbrake = buttonPressed(gamepad, normalized.handbrakeButton);
  result.nitro = buttonPressed(gamepad, normalized.nitroButton);
  result.fire = buttonPressed(gamepad, normalized.fireButton);
  result.camera = buttonPressed(gamepad, normalized.cameraButton);
  result.turret = strongest(rightStickTurret, bumperTurret);
  result.reset = buttonPressed(gamepad, normalized.resetButton);
  result.bank = buttonPressed(gamepad, 10);
  result.settings = buttonPressed(gamepad, normalized.settingsButton);
  return result;
}

/**
 * Inner dead zone, outer saturation and response exponent for the steering
 * stick, in that order. The output is continuous at the dead-zone edge, so
 * small corrections never jump.
 */
export function shapeSteeringAxis(value: number, tuning: GamepadTuning = DEFAULT_GAMEPAD_TUNING): number {
  const deadZone = Math.max(0, Math.min(0.4, tuning.steerDeadZone));
  const travel = applyGamepadDeadZone(value, deadZone);
  if (travel === 0) return 0;
  const usable = Math.max(0.05, 1 - STEER_OUTER_DEAD_ZONE / Math.max(0.05, 1 - deadZone));
  const saturated = Math.min(1, Math.abs(travel) / usable);
  const exponent = Math.max(0.5, Math.min(2.2, tuning.steerLinearity));
  return Math.sign(travel) * saturated ** exponent;
}

export function normalizeGamepadTuning(value: Partial<GamepadTuning> | null | undefined): GamepadTuning {
  const pick = (candidate: unknown, min: number, max: number, fallback: number): number => {
    const number = Number(candidate);
    return Number.isFinite(number) ? Math.max(min, Math.min(max, number)) : fallback;
  };
  return {
    steerDeadZone: pick(value?.steerDeadZone, 0, 0.4, DEFAULT_GAMEPAD_TUNING.steerDeadZone),
    steerLinearity: pick(value?.steerLinearity, 0.5, 2.2, DEFAULT_GAMEPAD_TUNING.steerLinearity),
    triggerDeadZone: pick(value?.triggerDeadZone, 0, 0.3, DEFAULT_GAMEPAD_TUNING.triggerDeadZone),
    vibration: pick(value?.vibration, 0, 1, DEFAULT_GAMEPAD_TUNING.vibration),
  };
}

export function defaultGamepadMapping(): GamepadMapping {
  return {
    ...DEFAULT_GAMEPAD_MAPPING,
    throttle: { ...DEFAULT_GAMEPAD_MAPPING.throttle },
    brake: { ...DEFAULT_GAMEPAD_MAPPING.brake },
  };
}

export function gamepadAnalogBindingValue(binding: GamepadAnalogBinding): string {
  return `${binding.source}:${binding.index}`;
}

export function parseGamepadAnalogBinding(value: string): GamepadAnalogBinding | null {
  const match = /^(button|axis-positive|axis-negative|axis-range):(\d{1,2})$/.exec(value);
  if (!match) return null;
  const index = Number(match[2]);
  if (!Number.isInteger(index) || index < 0 || index > 31) return null;
  return { source: match[1] as GamepadAnalogSource, index };
}

export function normalizeGamepadMapping(value: Partial<GamepadMapping> | null | undefined): GamepadMapping {
  const fallback = DEFAULT_GAMEPAD_MAPPING;
  return {
    version: 1,
    steerAxis: safeIndex(value?.steerAxis, fallback.steerAxis, 15),
    steerInverted: typeof value?.steerInverted === 'boolean' ? value.steerInverted : fallback.steerInverted,
    throttle: normalizeAnalogBinding(value?.throttle, fallback.throttle),
    brake: normalizeAnalogBinding(value?.brake, fallback.brake),
    handbrakeButton: safeIndex(value?.handbrakeButton, fallback.handbrakeButton, 31),
    nitroButton: safeIndex(value?.nitroButton, fallback.nitroButton, 31),
    fireButton: safeIndex(value?.fireButton, fallback.fireButton, 31),
    cameraButton: safeIndex(value?.cameraButton, fallback.cameraButton, 31),
    turretAxis: safeIndex(value?.turretAxis, fallback.turretAxis, 15),
    turretInverted: typeof value?.turretInverted === 'boolean' ? value.turretInverted : fallback.turretInverted,
    resetButton: safeIndex(value?.resetButton, fallback.resetButton, 31),
    settingsButton: safeIndex(value?.settingsButton, fallback.settingsButton, 31),
  };
}

export function loadGamepadMapping(controllerId: string): GamepadMapping | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(GAMEPAD_MAPPING_STORAGE_KEY) ?? '{}') as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const value = (parsed as Record<string, unknown>)[normalizedGamepadId(controllerId)];
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    return normalizeGamepadMapping(value as Partial<GamepadMapping>);
  } catch {
    return null;
  }
}

function saveGamepadMapping(controllerId: string, mapping: GamepadMapping): void {
  try {
    const raw = JSON.parse(localStorage.getItem(GAMEPAD_MAPPING_STORAGE_KEY) ?? '{}') as unknown;
    const mappings = raw && typeof raw === 'object' && !Array.isArray(raw)
      ? raw as Record<string, unknown>
      : {};
    mappings[normalizedGamepadId(controllerId)] = normalizeGamepadMapping(mapping);
    localStorage.setItem(GAMEPAD_MAPPING_STORAGE_KEY, JSON.stringify(mappings));
  } catch {
    // The active mapping still works when private storage is unavailable.
  }
}

function forgetGamepadMapping(controllerId: string): void {
  try {
    const raw = JSON.parse(localStorage.getItem(GAMEPAD_MAPPING_STORAGE_KEY) ?? '{}') as unknown;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return;
    const mappings = raw as Record<string, unknown>;
    delete mappings[normalizedGamepadId(controllerId)];
    localStorage.setItem(GAMEPAD_MAPPING_STORAGE_KEY, JSON.stringify(mappings));
  } catch {
    // Resetting the active session remains useful without persistence.
  }
}

export function applyGamepadDeadZone(value: number, deadZone: number): number {
  const safeValue = Number.isFinite(value) ? Math.max(-1, Math.min(1, value)) : 0;
  const safeDeadZone = Math.max(0, Math.min(0.95, deadZone));
  if (Math.abs(safeValue) <= safeDeadZone) return 0;
  return Math.sign(safeValue) * ((Math.abs(safeValue) - safeDeadZone) / (1 - safeDeadZone));
}

export function applyTriggerDeadZone(value: number, deadZone = 0.06): number {
  const safeValue = Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0;
  const safeDeadZone = Math.max(0, Math.min(0.95, deadZone));
  if (safeValue <= safeDeadZone) return 0;
  return (safeValue - safeDeadZone) / (1 - safeDeadZone);
}

function readBrowserGamepads(): readonly (GamepadLike | null)[] {
  if (typeof navigator.getGamepads !== 'function') return [];
  try {
    return navigator.getGamepads();
  } catch {
    return [];
  }
}

function isConnected(gamepad: GamepadLike | null | undefined): gamepad is GamepadLike {
  return Boolean(gamepad && gamepad.connected !== false);
}

function hasDrivingIntent(
  gamepad: GamepadLike,
  mapping: GamepadMapping,
  scratch: MappedGamepadInput,
  tuning: GamepadTuning,
): boolean {
  const input = mapGamepadInputInto(gamepad, mapping, scratch, tuning);
  return Math.abs(input.steer) > 0.08
    || Math.abs(input.turret) > 0.12
    || input.throttle > 0.08
    || input.brake > 0.08
    || input.handbrake
    || input.nitro
    || input.fire
    || input.camera
    || input.reset
    || input.settings;
}

function createMappedGamepadInput(): MappedGamepadInput {
  return {
    ...EMPTY_DRIVE_INPUT,
    camera: false,
    settings: false,
  };
}

function gamepadName(gamepad: GamepadLike): string {
  const name = gamepad.id.replace(/\s+/g, ' ').trim().slice(0, 56);
  return name || `Controller ${gamepad.index + 1}`;
}

function axisValue(gamepad: GamepadLike, index: number): number {
  return axisNumber(gamepad.axes[index]);
}

function axisNumber(value: unknown): number {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? Math.max(-1, Math.min(1, number)) : 0;
}

function analogBindingValue(gamepad: GamepadLike, binding: GamepadAnalogBinding, deadZone: number): number {
  if (binding.source === 'button') return applyTriggerDeadZone(buttonValue(gamepad, binding.index), deadZone);
  const axis = axisValue(gamepad, binding.index);
  if (binding.source === 'axis-range') return applyTriggerDeadZone((axis + 1) * 0.5, deadZone);
  if (binding.source === 'axis-negative') return applyTriggerDeadZone(Math.max(0, -axis), deadZone);
  return applyTriggerDeadZone(Math.max(0, axis), deadZone);
}

function swallow(result: unknown): void {
  if (result && typeof (result as Promise<unknown>).catch === 'function') {
    void (result as Promise<unknown>).catch(() => undefined);
  }
}

function buttonValue(gamepad: GamepadLike, index: number): number {
  const button = gamepad.buttons[index];
  if (!button) return 0;
  const value = Number(button.value);
  if (Number.isFinite(value) && value !== 0) return Math.max(0, Math.min(1, value));
  return button.pressed ? 1 : 0;
}

function buttonPressed(gamepad: GamepadLike, index: number): boolean {
  return Boolean(gamepad.buttons[index]?.pressed) || buttonValue(gamepad, index) > 0.5;
}

function strongest(first: number, second: number): number {
  return Math.abs(first) >= Math.abs(second) ? first : second;
}

function normalizeAnalogBinding(value: unknown, fallback: GamepadAnalogBinding): GamepadAnalogBinding {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return { ...fallback };
  const candidate = value as Partial<GamepadAnalogBinding>;
  if (!['button', 'axis-positive', 'axis-negative', 'axis-range'].includes(String(candidate.source))) {
    return { ...fallback };
  }
  return {
    source: candidate.source as GamepadAnalogSource,
    index: safeIndex(candidate.index, fallback.index, 31),
  };
}

function safeIndex(value: unknown, fallback: number, maximum: number): number {
  const number = Number(value);
  return Number.isInteger(number) ? Math.max(0, Math.min(maximum, number)) : fallback;
}

function normalizedGamepadId(value: string): string {
  const id = value.replace(/\s+/g, ' ').trim().toLowerCase().slice(0, 120);
  return id || 'unknown-controller';
}
